"use client";

import { useHeaderMotion } from "@/components/motion/header-motion";
import { websiteIcon } from "@/icons/icon-catalogue";
import { useState } from "react";
import {
  groupChildren,
  type MenuOptions,
  type NavEntry,
} from "@/utils/header-navigation";
import NavigationLink from "./NavigationLink";
import NavIcon from "./NavIcon";
import { useMenuFormatting, useMenuLayout } from "./NavigationFormatting";

interface ContentProps {
  path: string | null;
  onNavigate: () => void;
}

function PanelLink({
  entry,
  path,
  onNavigate,
  className = "re-nav-panel-link",
  icons = false,
  role = "panel",
}: ContentProps & {
  entry: NavEntry;
  className?: string;
  icons?: boolean;
  role?: import("@/cms/menu-formatting").MenuRole;
}) {
  const format = useMenuFormatting();
  return (
    <NavigationLink
      {...format(role, entry.id)}
      link={entry.link!}
      className={className}
      current={path === entry.link!.href}
      onClick={onNavigate}
    >
      {(icons || websiteIcon(entry.iconId)) &&
        entry.iconId !== null &&
        (entry.icon || entry.iconId) && (
          <NavIcon name={entry.icon} iconId={entry.iconId} />
        )}
      <span>{entry.label}</span>
    </NavigationLink>
  );
}

/** A group's children: loose links, then headings as small columns. */
function GroupBody({
  entry,
  path,
  onNavigate,
  headingColumns,
}: ContentProps & { entry: NavEntry; headingColumns: boolean }) {
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  const children = groupChildren(entry);
  const loose = children.filter((child) => child.kind !== "heading");
  const headings = children.filter((child) => child.kind === "heading");
  return (
    <>
      {!!loose.length && (
        <div {...layoutFormat("child")} className="re-nav-panel-links">
          {loose.map((child) => (
            <PanelLink
              key={child.key}
              entry={child}
              path={path}
              onNavigate={onNavigate}
              role="child"
              className="re-nav-panel-sublink"
            />
          ))}
        </div>
      )}
      {!!headings.length && (
        <div
          className="re-nav-panel-headings"
          data-columns={headingColumns || undefined}
        >
          {headings.map((heading) => (
            <div
              key={heading.key}
              {...layoutFormat("grandchild")}
              className="re-nav-panel-heading-block"
            >
              <p
                {...format("heading", heading.id)}
                className="re-nav-panel-heading"
              >
                {heading.label}
              </p>
              {heading.children.map((child) => (
                <PanelLink
                  key={child.key}
                  entry={child}
                  path={path}
                  onNavigate={onNavigate}
                  role="grandchild"
                  className="re-nav-panel-sublink"
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function AccordionList({
  items,
  path,
  onNavigate,
  headingColumns,
  idPrefix,
}: ContentProps & {
  items: NavEntry[];
  headingColumns: boolean;
  idPrefix: string;
}) {
  const { motion, useReducedMotion } = useHeaderMotion();
  const reduced = useReducedMotion();
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  const [open, setOpen] = useState<string[]>([]);
  return (
    <div {...layoutFormat("panel")} className="re-nav-panel-list">
      {items.map((item) => {
        if (!item.children.length)
          return (
            <PanelLink
              key={item.key}
              entry={item}
              path={path}
              onNavigate={onNavigate}
              icons
            />
          );
        const expanded = open.includes(item.key);
        const bodyId = `${idPrefix}-${item.key}`;
        const toggle = () =>
          setOpen((keys) =>
            expanded
              ? keys.filter((key) => key !== item.key)
              : [...keys, item.key],
          );
        return (
          <div
            key={item.key}
            className="re-nav-panel-group"
            data-open={expanded || undefined}
          >
            <button
              type="button"
              {...format(item.kind === "group" ? "group" : "parent", item.id)}
              className="re-nav-panel-group-row re-nav-panel-group-button"
              aria-expanded={expanded}
              aria-controls={bodyId}
              onClick={toggle}
            >
              <span>{item.label}</span>
              <NavIcon name="chevron" className="re-nav-chevron" />
            </button>
            <motion.div
              id={bodyId}
              className="re-nav-panel-group-body"
              initial={false}
              animate={{
                height: expanded ? "auto" : 0,
                opacity: expanded ? 1 : 0,
              }}
              transition={{ duration: reduced ? 0 : 0.24, ease: "easeInOut" }}
              aria-hidden={!expanded || undefined}
              inert={!expanded || undefined}
            >
              <div className="re-nav-panel-group-body-inner">
                <GroupBody
                  entry={item}
                  path={path}
                  onNavigate={onNavigate}
                  headingColumns={headingColumns}
                />
              </div>
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}

function GroupTitle({
  entry,
  path,
  onNavigate,
}: ContentProps & { entry: NavEntry }) {
  const format = useMenuFormatting();
  return entry.link ? (
    <PanelLink
      entry={entry}
      path={path}
      onNavigate={onNavigate}
      role={entry.kind === "group" ? "group" : "parent"}
      className="re-nav-panel-column-title"
    />
  ) : (
    <p {...format("group", entry.id)} className="re-nav-panel-column-title">
      {entry.label}
    </p>
  );
}

/** Drawer with column groups: loose links first, then every group open with its title. */
function StackedGroups({
  items,
  path,
  onNavigate,
}: ContentProps & { items: NavEntry[] }) {
  const layoutFormat = useMenuLayout();
  return (
    <div {...layoutFormat("panel")} className="re-nav-panel-list">
      {items.map((item) =>
        item.children.length ? (
          <div key={item.key} className="re-nav-panel-stack">
            <GroupTitle entry={item} path={path} onNavigate={onNavigate} />
            <GroupBody
              entry={item}
              path={path}
              onNavigate={onNavigate}
              headingColumns
            />
          </div>
        ) : (
          <PanelLink
            key={item.key}
            entry={item}
            path={path}
            onNavigate={onNavigate}
            icons
          />
        ),
      )}
    </div>
  );
}

/** Slide-down and full-screen columns: loose links as »Genveje«, one column per group, headings as sub-columns. */
function Columns({
  items,
  path,
  onNavigate,
  preserveOrder,
}: ContentProps & { items: NavEntry[]; preserveOrder: boolean }) {
  const format = useMenuFormatting();
  const layoutFormat = useMenuLayout();
  const columns: { key: string; group?: NavEntry; links?: NavEntry[] }[] = [];
  for (const item of items) {
    if (item.children.length) columns.push({ key: item.key, group: item });
    else {
      const last = columns[columns.length - 1];
      if (last?.links) last.links.push(item);
      else columns.push({ key: item.key, links: [item] });
    }
  }
  const ordered: typeof columns = preserveOrder
    ? columns
    : [
        {
          key: "shortcuts",
          links: items.filter((item) => !item.children.length),
        },
        ...items
          .filter((item) => item.children.length)
          .map((group) => ({ key: group.key, group })),
      ];
  return (
    <div {...layoutFormat("panel")} className="re-nav-panel-columns">
      {ordered.map((column) => {
        if (column.links)
          return column.links.length ? (
            <div key={column.key} className="re-nav-panel-column">
              <p className="re-nav-panel-column-title">Genveje</p>
              <div {...layoutFormat("panel")} className="re-nav-panel-links">
                {column.links.map((item) => (
                  <PanelLink
                    key={item.key}
                    entry={item}
                    path={path}
                    onNavigate={onNavigate}
                    className="re-nav-panel-shortcut"
                    icons
                  />
                ))}
              </div>
            </div>
          ) : null;
        const group = column.group!;
        const children = groupChildren(group);
        const loose = children.filter((child) => child.kind !== "heading");
        const headings = children.filter((child) => child.kind === "heading");
        return (
          <div key={group.key} className="re-nav-panel-column-group">
            <GroupTitle entry={group} path={path} onNavigate={onNavigate} />
            <div className="re-nav-panel-subcolumns">
              {!!loose.length && (
                <div {...layoutFormat("child")} className="re-nav-panel-links">
                  {loose.map((child) => (
                    <PanelLink
                      key={child.key}
                      entry={child}
                      path={path}
                      onNavigate={onNavigate}
                      role="child"
                      className="re-nav-panel-sublink"
                    />
                  ))}
                </div>
              )}
              {headings.map((heading) => (
                <div
                  key={heading.key}
                  {...layoutFormat("grandchild")}
                  className="re-nav-panel-links"
                >
                  <p
                    {...format("heading", heading.id)}
                    className="re-nav-panel-heading"
                  >
                    {heading.label}
                  </p>
                  {heading.children.map((child) => (
                    <PanelLink
                      key={child.key}
                      entry={child}
                      path={path}
                      onNavigate={onNavigate}
                      role="grandchild"
                      className="re-nav-panel-sublink"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const loosesFirst = (items: NavEntry[]) => [
  ...items.filter((item) => !item.children.length),
  ...items.filter((item) => item.children.length),
];

export type PanelMode = "drawer" | "slideDown" | "fullscreen" | "sheet";

/** Below the breakpoint the drawer and the slide-down panel become a full-width sheet. */
export function panelMode(
  style: MenuOptions["style"],
  desktop: boolean,
): PanelMode {
  return desktop || style === "fullscreen" ? style : "sheet";
}

/** The panel's navigation for one mode: mobile is always an accordion; desktop follows `groups`. */
export default function NavigationPanelContent({
  options,
  desktop,
  desktopItems,
  mobileItems,
  path,
  onNavigate,
  idPrefix,
}: {
  options: MenuOptions;
  desktop: boolean;
  desktopItems: NavEntry[];
  mobileItems: NavEntry[];
  path: string | null;
  onNavigate: () => void;
  idPrefix: string;
}) {
  const content = { path, onNavigate };
  if (!desktop)
    return (
      <AccordionList
        items={mobileItems}
        headingColumns={false}
        idPrefix={idPrefix}
        {...content}
      />
    );
  if (options.groups === "columns" && options.style !== "drawer")
    return (
      <Columns
        items={desktopItems}
        preserveOrder={options.overflow}
        {...content}
      />
    );
  if (options.groups === "columns")
    return (
      <StackedGroups
        items={options.overflow ? desktopItems : loosesFirst(desktopItems)}
        {...content}
      />
    );
  return (
    <AccordionList
      items={
        options.style === "drawer" && !options.overflow
          ? loosesFirst(desktopItems)
          : desktopItems
      }
      headingColumns
      idPrefix={idPrefix}
      {...content}
    />
  );
}
